import React, { useEffect, useState } from 'react';
import { api } from './AdminApp';
import ImageField from './ImageField';
import Loader from '../Components/Loader/Loader';

export default function Settings() {
  const [form, setForm] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploads, setUploads] = useState(null);
  const [checking, setChecking] = useState(false);

  // Live check of where images are stored and which domain serves them.
  const checkUploads = async () => {
    setChecking(true);
    try {
      const d = await api('/admin/settings/uploads-status');
      setUploads(d.uploads);
    } catch (err) {
      setUploads({ error: err.message });
    }
    setChecking(false);
  };

  useEffect(() => {
    api('/admin/settings')
      .then((d) => {
        setForm({
          weekdays: d.settings.timing.weekdays || '',
          weekend: d.settings.timing.weekend || '',
        });
        setLogoUrl(d.settings.logo_url || null);
      })
      .catch((e) => setError(e.message));
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadLogo = async (file) => {
    setError('');
    setNotice('');
    const fd = new FormData();
    fd.append('logo', file);
    try {
      const d = await api('/admin/settings/logo', { method: 'POST', body: fd });
      setLogoUrl(d.logo_url || null);
      setLogoFile(null);
      setNotice(d.message || 'Logo updated.');
    } catch (err) {
      setError(err.message);
    }
  };

  const removeLogo = async () => {
    setError('');
    setNotice('');
    try {
      const d = await api('/admin/settings/logo/delete', {
        method: 'POST',
        body: JSON.stringify({}),
      });
      setLogoUrl(null);
      setLogoFile(null);
      setNotice(d.message || 'Logo removed.');
    } catch (err) {
      setError(err.message);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const d = await api('/admin/settings', {
        method: 'POST',
        body: JSON.stringify({
          timing: {
            weekdays: form.weekdays,
            weekend: form.weekend,
          },
        }),
      });
      setNotice(d.message || 'Saved');
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  if (!form) {
    return (
      <div>
        <h1 className="admin-page-title">Settings</h1>
        {error ? (
          <div className="admin-alert admin-alert-error">{error}</div>
        ) : (
          <Loader inline status="Loading settings" />
        )}
      </div>
    );
  }

  return (
    <div>
      <h1 className="admin-page-title">Settings</h1>
      {error && <div className="admin-alert admin-alert-error">{error}</div>}
      {notice && <div className="admin-alert admin-alert-success">{notice}</div>}

      <form className="admin-card admin-form" onSubmit={save}>
        <h2>Site logo</h2>
        <p className="admin-muted">
          Shown in the site header. PNG with transparency works best.
        </p>
        <ImageField
          label="Logo"
          url={logoUrl}
          file={logoFile}
          onPick={(f) => {
            setLogoFile(f);
            if (f) uploadLogo(f);
          }}
          onRemove={removeLogo}
          hint="PNG, JPEG or WebP · up to 10 MB"
        />

        <h2 style={{ marginTop: 24 }}>Clinic timing</h2>
        <p className="admin-muted">
          Shown in the footer and on the Contact page.
        </p>
        <div className="admin-form-grid">
          <label>
            Weekdays
            <input value={form.weekdays} onChange={set('weekdays')} required />
          </label>
          <label>
            Weekend / Sunday
            <input value={form.weekend} onChange={set('weekend')} required />
          </label>
        </div>

        <div className="admin-form-actions">
          <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save settings'}
          </button>
        </div>
      </form>

      <div className="admin-card" style={{ marginTop: 24 }}>
        <h2>Images &amp; storage</h2>
        <p className="admin-muted">
          Where uploaded images are stored and which domain serves them. Run this if an image
          appears broken on the live site.
        </p>
        <button type="button" className="admin-btn" onClick={checkUploads} disabled={checking}>
          {checking ? 'Checking…' : 'Check images'}
        </button>

        {uploads && uploads.error && (
          <div className="admin-alert admin-alert-error" style={{ marginTop: 12 }}>
            {uploads.error}
          </div>
        )}

        {uploads && !uploads.error && (
          <div className="admin-report" style={{ marginTop: 14 }}>
            <div>
              <span>Images served from</span>
              <strong>{uploads.publicBase}</strong>
            </div>
            <div>
              <span>Stored in</span>
              <strong>
                {uploads.dir} {uploads.dirWritable ? '' : '(not writable!)'} · {uploads.fileCount}{' '}
                files
              </strong>
            </div>
            {uploads.dirMode && uploads.dirMode !== 'default' && (
              <div>
                <span>Storage</span>
                <strong>
                  {uploads.dirMode === 'persistent'
                    ? 'persistent domain folder — survives redeploys'
                    : uploads.dirMode === 'explicit'
                      ? 'set explicitly via UPLOAD_DIR'
                      : 'app folder'}
                </strong>
              </div>
            )}
            {uploads.legacyMirror && (
              <div>
                <span>Older images copied from</span>
                <strong>{uploads.legacyMirror}</strong>
              </div>
            )}
            {uploads.logo && (
              <div>
                <span>Current logo</span>
                <strong className={uploads.logo.served ? 'admin-ok' : 'admin-bad'}>
                  {uploads.logo.served
                    ? `loads OK (${uploads.logo.status})`
                    : `NOT loading (${uploads.logo.status || 'unreachable'})`}
                  {' — '}
                  {uploads.logo.url}
                </strong>
              </div>
            )}
            {(uploads.recommendation || (uploads.notes && uploads.notes.length > 0)) && (
              <div className="admin-report-warn">
                {uploads.recommendation}
                {uploads.recommendation && uploads.notes && uploads.notes.length > 0 ? ' ' : ''}
                {uploads.notes && uploads.notes.join(' ')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
